import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiClientError } from './api';
import { ErrorMessage, StatusBadge } from './components';
import { CompanyPicker } from './DeviceForm';
import { SearchSelect } from './SearchSelect';
import { usePermissions } from './permissions';
import type { Contact, ContactOverview, ContactSyncStatus } from './types';

const identityTypes = { EMAIL: 'E-Mail', PHONE: 'Telefon', MOBILE: 'Mobiltelefon' };
type Identity = Contact['identities'][number];

export function ContactDetails({
  contact: initial,
  onChanged,
}: {
  contact: ContactOverview;
  onChanged: () => void;
}) {
  const can = usePermissions();
  const [contact, setContact] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [notice, setNotice] = useState('');
  const [identity, setIdentity] = useState<Identity | 'new'>();
  const [companyId, setCompanyId] = useState(initial.companyId ?? '');
  const [assigning, setAssigning] = useState(false);
  const [identityType, setIdentityType] = useState('EMAIL');
  useEffect(() => {
    setContact(initial);
    setCompanyId(initial.companyId ?? '');
  }, [initial]);
  const writable = contact.status !== 'ARCHIVED' && !contact.mergeTargetId;
  const reload = async () => {
    const fresh = await api<ContactOverview>(`/api/contacts/${contact.id}/overview`);
    setContact(fresh);
    setCompanyId(fresh.companyId ?? '');
    onChanged();
  };
  const perform = async (command: () => Promise<unknown>, message: string, after?: () => void) => {
    setBusy(true);
    setError(undefined);
    setNotice('');
    try {
      await command();
      after?.();
      await reload();
      setNotice(message);
    } catch (e) {
      setError(e);
      if (e instanceof ApiClientError && e.status === 409) {
        try {
          await reload();
        } catch {
          /* Keep the original conflict visible. */
        }
      }
    } finally {
      setBusy(false);
    }
  };
  const saveIdentity = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = {
      type: identityType,
      value: String(form.get('value')).trim(),
      label: String(form.get('label') ?? '').trim() || null,
      isPrimary: form.get('isPrimary') === 'on',
    };
    void perform(
      () =>
        api(
          `/api/contacts/${contact.id}/identities${identity && identity !== 'new' ? `/${identity.id}` : ''}`,
          {
            method: identity === 'new' ? 'POST' : 'PATCH',
            body: JSON.stringify({
              ...body,
              ...(identity && identity !== 'new' ? { revision: identity.revision } : {}),
            }),
          },
        ),
      'Kontaktweg gespeichert.',
      () => setIdentity(undefined),
    );
  };
  const assignment = (item: ContactSyncStatus, status: 'ENABLED' | 'DISABLED') =>
    perform(
      () =>
        api('/api/assignments', {
          method: 'PUT',
          body: JSON.stringify({
            entityType: 'CONTACT',
            entityId: contact.id,
            integrationInstanceId: item.instanceId,
            status,
            ...(item.directAssignmentVersion
              ? { expectedVersion: item.directAssignmentVersion }
              : {}),
          }),
        }),
      status === 'ENABLED'
        ? 'Synchronisierung für diesen Kontakt aktiviert; Übernahme ausstehend.'
        : 'Synchronisierung für diesen Kontakt deaktiviert; Übernahme ausstehend.',
    );
  return (
    <>
      <ErrorMessage error={error} />
      {notice && (
        <p role="status" className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
          {notice}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={contact.status} />
        {contact.jobTitle && <span className="text-sm text-muted">{contact.jobTitle}</span>}
      </div>
      <section>
        <h3 className="section-title">Kunde</h3>
        {contact.organization ? (
          <Link className="text-sm text-accent" to={`/companies/${contact.organization.id}`}>
            {contact.organization.name}
          </Link>
        ) : (
          <p className="text-sm text-muted">Keinem Kunden zugeordnet.</p>
        )}
        {writable && can('core:contacts:write') && can('core:companies:read') && (
          <div className="mt-3">
            {assigning ? (
              <form
                className="space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  void perform(
                    () =>
                      api(`/api/contacts/${contact.id}`, {
                        method: 'PATCH',
                        revision: contact.revision,
                        body: JSON.stringify({ companyId: companyId || null }),
                      }),
                    'Kundenzuordnung gespeichert.',
                    () => setAssigning(false),
                  );
                }}
              >
                <CompanyPicker
                  value={companyId}
                  label={contact.organization?.name}
                  onChange={setCompanyId}
                />
                <p className="text-xs text-muted">
                  Beim Wechsel werden bisherige aktive Kundenzuordnungen beendet. Geerbte Freigaben
                  richten sich anschließend nach dem neuen Kunden.
                </p>
                <div className="flex gap-2">
                  <button className="button" disabled={busy}>
                    Zuordnung speichern
                  </button>
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={busy}
                    onClick={() => setAssigning(false)}
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            ) : (
              <button
                className="button-secondary"
                disabled={busy}
                onClick={() => setAssigning(true)}
              >
                {contact.organization ? 'Kundenzuordnung ändern' : 'Kunden zuweisen'}
              </button>
            )}
          </div>
        )}
      </section>
      <section>
        <h3 className="section-title">Kontaktwege</h3>
        <div className="space-y-3">
          {contact.identities
            .filter((item) => !item.revokedAt)
            .map((item) => (
              <div key={item.id} className="rounded-lg border border-line p-3">
                <div className="flex flex-wrap justify-between gap-2 text-xs text-muted">
                  <span>
                    {identityTypes[item.type as keyof typeof identityTypes]}
                    {item.label ? ` · ${item.label}` : ''}
                  </span>
                  <span>
                    {item.isPrimary ? 'Primär' : ''}
                    {item.verifiedAt ? ' · Verifiziert' : ''}
                  </span>
                </div>
                <p className="mt-1 break-all text-sm">{item.value}</p>
                {writable && can('core:contacts:write') && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      className="button-secondary"
                      disabled={busy}
                      onClick={() => {
                        setIdentity(item);
                        setIdentityType(item.type);
                      }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      className="button-secondary"
                      disabled={busy}
                      onClick={() =>
                        void perform(
                          () =>
                            api(`/api/contacts/${contact.id}/identities/${item.id}`, {
                              method: 'DELETE',
                              revision: item.revision,
                            }),
                          'Kontaktweg widerrufen.',
                        )
                      }
                    >
                      Widerrufen
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>
        {!contact.identities.some((item) => !item.revokedAt) && (
          <p className="text-sm text-muted">Keine Kontaktwege hinterlegt.</p>
        )}
        {identity ? (
          <form
            key={identity === 'new' ? 'new' : identity.id}
            className="mt-4 space-y-3 rounded-lg border border-line p-4"
            onSubmit={saveIdentity}
          >
            <label>
              <span className="label">Art</span>
              <SearchSelect
                className="input w-full"
                value={identityType}
                onValueChange={setIdentityType}
              >
                {Object.entries(identityTypes).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </SearchSelect>
            </label>
            <label>
              <span className="label">
                {identityType === 'EMAIL' ? 'E-Mail-Adresse' : 'Telefonnummer mit Landesvorwahl'} *
              </span>
              <input
                className="input w-full"
                name="value"
                required
                type={identityType === 'EMAIL' ? 'email' : 'tel'}
                maxLength={320}
                defaultValue={identity === 'new' ? '' : identity.value}
                placeholder={identityType === 'EMAIL' ? 'name@example.de' : '+49 30 123456'}
                autoFocus
              />
            </label>
            <label>
              <span className="label">Bezeichnung</span>
              <input
                className="input w-full"
                name="label"
                maxLength={100}
                defaultValue={identity === 'new' ? '' : (identity.label ?? '')}
                placeholder="z. B. Geschäftlich, Zentrale, Privat"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="isPrimary"
                defaultChecked={identity !== 'new' && identity.isPrimary}
              />
              Primärer Kontaktweg dieser Art
            </label>
            <div className="flex gap-2">
              <button className="button" disabled={busy}>
                {busy ? 'Speichert…' : 'Kontaktweg speichern'}
              </button>
              <button
                className="button-secondary"
                type="button"
                disabled={busy}
                onClick={() => setIdentity(undefined)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          writable &&
          can('core:contacts:write') && (
            <button
              className="button-secondary mt-3"
              disabled={busy}
              onClick={() => {
                setIdentity('new');
                setIdentityType('EMAIL');
              }}
            >
              Kontaktweg ergänzen
            </button>
          )
        )}
      </section>
      <section>
        <h3 className="section-title">Anwendungen und Synchronisierung</h3>
        <div className="space-y-3">
          {contact.syncStatuses.map((item) => (
            <div key={item.instanceId} className="rounded-lg border border-line p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <span className="text-sm font-medium">{item.instanceName}</span>
                <StatusBadge status={item.desired === 'DISABLED' ? 'DISABLED' : item.actual} />
              </div>
              <p className="mt-1 text-xs text-muted">
                {item.integrationName} · {item.environment} ·{' '}
                {item.source === 'COMPANY'
                  ? 'Freigabe über Kunde'
                  : item.source === 'CONTACT'
                    ? 'Direkte Freigabe'
                    : 'Keine Freigabe'}
              </p>
              {item.needsRetry && (
                <p className="mt-2 text-xs text-amber-800">Übernahme noch offen.</p>
              )}
              {item.lastSuccessAt && (
                <p className="mt-2 text-xs text-muted">
                  Zuletzt synchronisiert: {new Date(item.lastSuccessAt).toLocaleString('de-DE')}
                </p>
              )}
              {item.errorMessage && (
                <p className="mt-2 break-words text-xs text-red-700">{item.errorMessage}</p>
              )}
              {writable && can('core:integrations:write') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.desired === 'ENABLED' && item.canReconcile && item.assignmentVersion && (
                    <button
                      className="button-secondary"
                      disabled={busy}
                      onClick={() =>
                        void perform(
                          () =>
                            api(`/api/contacts/${contact.id}/sync`, {
                              method: 'POST',
                              revision: contact.revision,
                              idempotencyKey: crypto.randomUUID(),
                              body: JSON.stringify({
                                integrationInstanceId: item.instanceId,
                                assignmentVersion: item.assignmentVersion,
                              }),
                            }),
                          'Synchronisierung angefordert. Die Anwendung übernimmt den Kontakt asynchron.',
                        )
                      }
                    >
                      Jetzt synchronisieren
                    </button>
                  )}
                  {item.desired === 'ENABLED' ? (
                    <button
                      className="button-secondary"
                      disabled={busy}
                      onClick={() => void assignment(item, 'DISABLED')}
                    >
                      Synchronisierung deaktivieren
                    </button>
                  ) : (
                    item.canReconcile && (
                      <button
                        className="button-secondary"
                        disabled={busy}
                        onClick={() => void assignment(item, 'ENABLED')}
                      >
                        Synchronisierung aktivieren
                      </button>
                    )
                  )}
                </div>
              )}
              {!item.canReconcile && (
                <p className="mt-2 text-xs text-muted">
                  Manuelle Kontaktsynchronisierung nicht verfügbar.
                </p>
              )}
              <details className="mt-3 text-xs text-muted">
                <summary>Technische Details</summary>
                <p className="mt-2 break-all">
                  {item.externalType}: {item.externalId ?? 'Keine externe Verknüpfung'}
                  {item.errorCode ? ` · ${item.errorCode}` : ''}
                </p>
              </details>
            </div>
          ))}
        </div>
        {!contact.syncStatuses.length && (
          <p className="text-sm text-muted">Keine Anwendungen eingerichtet.</p>
        )}
        <p className="mt-3 text-xs text-muted">
          Deaktivieren gilt nur für diesen Kontakt und löscht keine Kontaktdaten. Die Anwendung
          meldet den Stand der Übernahme.
        </p>
      </section>
      <details className="technical">
        <summary>Technische Metadaten</summary>
        <p className="mt-3 break-all">
          Control-ID: {contact.id} · Revision: {contact.revision}
        </p>
      </details>
    </>
  );
}
